import { useEffect, useState, type ReactNode } from 'react';
import { BookOpen } from '../components/Icons';
import { api } from '@mastery/api-client-web';

type PublicLoadCacheEntry = {
  data: unknown;
  expiresAt: number;
};

// These endpoints are public catalog/configuration reads. Keeping a short-lived
// browser cache avoids repeating the same request while moving through the
// public catalog, while account-scoped endpoints below remain uncached.
const PUBLIC_LOAD_CACHE_TTL = 30_000;
const publicLoadCache = new Map<string, PublicLoadCacheEntry>();
const publicLoadInflight = new Map<string, Promise<unknown>>();

function isPublicLoadPath(path: string) {
  const pathname = path.split('?', 1)[0];
  return (
    pathname === '/site' ||
    pathname === '/plans' ||
    pathname === '/exams' ||
    pathname === '/library'
  );
}

function readPublicLoadCache<T>(path: string) {
  const cached = publicLoadCache.get(path);
  if (!cached) return undefined;
  if (cached.expiresAt <= Date.now()) {
    publicLoadCache.delete(path);
    return undefined;
  }
  return cached.data as T;
}

function publicLoad<T>(path: string, signal: AbortSignal) {
  const existing = publicLoadInflight.get(path);
  if (existing) return existing as Promise<T>;
  const request = api.get<T>(path, { signal });
  publicLoadInflight.set(path, request);
  void request.then(
    (data) => {
      publicLoadCache.set(path, { data, expiresAt: Date.now() + PUBLIC_LOAD_CACHE_TTL });
      if (publicLoadInflight.get(path) === request) publicLoadInflight.delete(path);
    },
    () => {
      if (publicLoadInflight.get(path) === request) publicLoadInflight.delete(path);
    },
  );
  return request;
}

export const labels: Record<string, string> = {
  'reading-use': '阅读与英语运用',
  reading: '阅读',
  'reading-writing': '阅读与写作',
  listening: '听力',
  writing: '写作',
  speaking: '口语',
  practice: '专项练习',
  mock: '模拟测试',
  adaptive: '个性化训练',
  in_progress: '进行中',
  submitted: '等待批改',
  graded: '已完成',
  pending: '待支付',
  paid: '已支付',
  refund_pending: '退款处理中',
  refunded: '已退款',
  cancelled: '已取消',
};
export const date = (value: string) =>
  new Date(value).toLocaleString('zh-CN', {
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
export const percent = (n: number) => Math.round(n * 100) + '%';
export const money = (n: number) => '¥' + (n / 100).toFixed(2);
export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return error ? (
    <div role="alert" className="error">
      {error instanceof Error ? error.message : String(error)}
      {onRetry && (
        <button className="text-button" onClick={onRetry}>
          重新加载
        </button>
      )}
    </div>
  ) : null;
}
export function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="empty">
      <BookOpen size={30} />
      <p>{children}</p>
    </div>
  );
}
export function useLoad<T>(path: string | null, deps: unknown[] = []) {
  const [state, setState] = useState<{
    path: string | null;
    data?: T;
    error?: unknown;
    loading: boolean;
  }>({ path, loading: !!path });
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!path) {
      setState({ path, loading: false });
      return;
    }
    let live = true;
    const controller = new AbortController();
    const cacheable = isPublicLoadPath(path);
    const skipCache = version > 0;
    if (cacheable && !skipCache) {
      const cached = readPublicLoadCache<T>(path);
      if (cached !== undefined) {
        setState({ path, data: cached, loading: false });
        return () => {
          live = false;
        };
      }
    }
    setState((previous) => ({
      path,
      data: previous.path === path ? previous.data : undefined,
      loading: true,
    }));
    const request = cacheable
      ? publicLoad<T>(path, controller.signal)
      : api.get<T>(path, { signal: controller.signal });
    request
      .then((v) => {
        if (live) setState({ path, data: v, loading: false });
      })
      .catch((e) => {
        if (live) setState((previous) => ({ ...previous, error: e, loading: false }));
      });
    return () => {
      live = false;
      // A public request may be shared by several pages. Let it finish so a
      // second consumer can reuse it; private requests retain cancellation.
      if (!cacheable) controller.abort();
    };
  }, [path, version, ...deps]);
  const current = state.path === path;
  return {
    data: current ? state.data : undefined,
    error: current ? state.error : undefined,
    loading: !!path && (!current || state.loading),
    reload: () => setVersion((v) => v + 1),
  };
}
